import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CBadge, CButton, CFormSelect, CNav, CNavItem, CNavLink, CSpinner } from '@coreui/react-pro'
import { looseKey, normalize } from 'src/components/common/playerMatching'
import ToastComponent from 'src/components/common/TaostComponent'

// Shared by every PlayHQ importer: how a PlayHQ record is matched to one of ours, what each row
// can be told to do, and the bits of table chrome they all use.

export const ACTIONS = {
  link: {
    label: 'Link to existing (keep my name)',
    help: 'Keeps your name; only remembers the PlayHQ id.',
  },
  update: {
    label: 'Link & rename from PlayHQ',
    help: 'Renames your record to PlayHQ’s name (and can change its grade/team).',
  },
  create: { label: 'Create new', help: 'Adds a new record from PlayHQ.' },
  skip: { label: 'Skip', help: 'Leaves this row out.' },
}

// How a row found its record, shown next to it so the admin can see what was guessed.
export const MATCH_BADGE = {
  linked: { label: 'Already linked', color: 'success' },
  name: { label: 'Matched by name', color: 'info' },
  loose: { label: 'Matched on name order', color: 'info' },
  grade: { label: 'Suggested by grade', color: 'info' },
  ambiguous: { label: 'Several matches', color: 'warning' },
  none: { label: 'No match found', color: 'secondary' },
  manual: { label: 'Chosen by you', color: 'primary' },
}

// "B Grade (80 Overs, 12 Players)" and "B Grade" are the same grade to an admin.
const withoutBrackets = (name) => String(name ?? '').replace(/\([^)]*\)/g, ' ')

/**
 * Finds the record a PlayHQ item is: by the PlayHQ id it was linked with, then by name, then by
 * name with its brackets dropped, then by name in any word order. More than one hit is reported
 * as ambiguous rather than guessed.
 */
export const matchRecord = (records, playhqId, name, getName) => {
  const linked = playhqId && records.find((r) => r.playhq_id && r.playhq_id === playhqId)
  if (linked) return { record: linked, how: 'linked' }

  const tries = [
    ['name', (r) => normalize(getName(r)) === normalize(name)],
    ['name', (r) => normalize(withoutBrackets(getName(r))) === normalize(withoutBrackets(name))],
    ['loose', (r) => looseKey(withoutBrackets(getName(r))) === looseKey(withoutBrackets(name))],
  ]
  for (const [how, test] of tries) {
    const hits = records.filter(test)
    if (hits.length === 1) return { record: hits[0], how }
    if (hits.length > 1) return { record: null, how: 'ambiguous', candidates: hits }
  }
  return { record: null, how: 'none' }
}

/** Ids picked by more than one row - two PlayHQ records cannot both be the same one of ours. */
export const duplicateTargets = (rows, targetKey = 'target_id') => {
  const seen = new Map()
  rows.forEach((row) => {
    if (row.action === 'skip' || row.action === 'create' || !row[targetKey]) return
    const key = String(row[targetKey])
    seen.set(key, (seen.get(key) || 0) + 1)
  })
  return new Set([...seen].filter(([, count]) => count > 1).map(([key]) => key))
}

export const ActionSelect = ({
  value,
  onChange,
  allowed = ['link', 'update', 'create', 'skip'],
}) => (
  <CFormSelect
    size="sm"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ minWidth: 170 }}
  >
    {allowed.map((key) => (
      <option key={key} value={key}>
        {ACTIONS[key].label}
      </option>
    ))}
  </CFormSelect>
)

export const RecordSelect = ({ value, onChange, records, getLabel, placeholder, invalid }) => (
  <CFormSelect
    size="sm"
    value={value || ''}
    invalid={invalid}
    onChange={(e) => onChange(e.target.value)}
    style={{ minWidth: 200 }}
  >
    <option value="">{placeholder}</option>
    {records.map((r) => (
      <option key={r.id} value={r.id}>
        {getLabel(r)}
      </option>
    ))}
  </CFormSelect>
)

export const MatchBadge = ({ how }) => {
  const meta = MATCH_BADGE[how] || MATCH_BADGE.none
  return (
    <CBadge color={meta.color} shape="rounded-pill" className="fw-normal">
      {meta.label}
    </CBadge>
  )
}

export const FilterPills = ({ filters, value, onChange }) => (
  <div className="d-flex gap-2 flex-wrap mb-3">
    {filters.map((f) => (
      <CButton
        key={f.key}
        size="sm"
        color={value === f.key ? 'primary' : 'secondary'}
        variant={value === f.key ? undefined : 'outline'}
        onClick={() => onChange(f.key)}
      >
        {f.label} <span className="ms-1 opacity-75">{f.count}</span>
      </CButton>
    ))}
  </div>
)

/**
 * Splits a tab into what is still to import and what already is, so the admin can see at a
 * glance what an earlier import did. `noun` names the rows ("matches", "squads").
 */
export const LinkedViewTabs = ({ value, onChange, counts, noun }) => (
  <CNav variant="pills" className="mb-3 gap-2" role="tablist">
    {[
      { key: 'pending', label: 'Not linked', count: counts.pending },
      { key: 'linked', label: 'Already linked', count: counts.linked },
    ].map((v) => (
      <CNavItem key={v.key}>
        <CNavLink
          active={value === v.key}
          onClick={() => onChange(v.key)}
          style={{ cursor: 'pointer' }}
          title={
            v.key === 'pending'
              ? `PlayHQ ${noun} not imported yet`
              : `PlayHQ ${noun} you already imported`
          }
        >
          {v.label}
          <CBadge
            color={value === v.key ? 'light' : 'secondary'}
            textColor={value === v.key ? 'dark' : undefined}
            shape="rounded-pill"
            className="ms-2 fw-normal"
          >
            {v.count}
          </CBadge>
        </CNavLink>
      </CNavItem>
    ))}
  </CNav>
)

// The values a filter can take, each with how many rows have it.
export const optionsOf = (rows, valueOf) => {
  const counts = new Map()
  rows.forEach((row) => {
    const value = valueOf(row)
    counts.set(value, (counts.get(value) || 0) + 1)
  })
  return [...counts.entries()].map(([value, count]) => ({ value, count }))
}

export const LoadingBlock = ({ text }) => (
  <div className="d-flex align-items-center gap-2 py-5 justify-content-center text-medium-emphasis">
    <CSpinner size="sm" /> {text}
  </div>
)

export const tableHeadStyle = { backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }

// Accent for a row by whether it is ready to import, needs the admin, or is left out.
export const ROW_TONE = {
  ready: '#198754',
  attention: '#b45309',
  skipped: '#adb5bd',
}

// Skipped rows are tinted rather than faded, so their checkboxes and selects stay readable.
export const rowStyle = (tone) => ({
  borderLeft: `3px solid ${ROW_TONE[tone]}`,
  ...(tone === 'skipped' ? { backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' } : {}),
})

// The message a failed request carries, whatever shape it came back in.
export const errorsOf = (res, fallback = 'Something went wrong. Please try again.') => {
  if (res?.errors?.length) return res.errors
  if (res?.message) return [res.message]
  return [fallback]
}

/* ------------------------------------------------------------------ */
/*  Bulk selection                                                     */
/* ------------------------------------------------------------------ */

// Block, so the box is not sat on a text baseline (which pushed it below the header text).
const CHECKBOX_STYLE = { display: 'block', width: 18, height: 18, cursor: 'pointer' }

// The checkbox column. Inline so no table stylesheet can bottom-align the header checkbox.
export const CHECK_CELL_STYLE = { width: 44, verticalAlign: 'middle' }

/** Which rows are ticked, by row key. Ticks survive filtering; a reload clears them. */
export const useRowSelection = () => {
  const [selected, setSelected] = useState(() => new Set())
  const toggle = useCallback(
    (key) =>
      setSelected((current) => {
        const next = new Set(current)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      }),
    [],
  )
  const setMany = useCallback(
    (keys, on) =>
      setSelected((current) => {
        const next = new Set(current)
        keys.forEach((key) => (on ? next.add(key) : next.delete(key)))
        return next
      }),
    [],
  )
  const clear = useCallback(() => setSelected(new Set()), [])
  return { selected, toggle, setMany, clear }
}

export const RowCheckbox = ({ checked, onChange, title }) => (
  <input
    type="checkbox"
    className="form-check-input m-0"
    style={CHECKBOX_STYLE}
    checked={checked}
    onChange={onChange}
    title={title}
    aria-label={title || 'Select row'}
  />
)

/** Ticks or unticks every row shown. Shows a dash when only some of them are ticked. */
export const SelectAllCheckbox = ({ keys, selection }) => {
  const ref = useRef(null)
  const ticked = keys.filter((key) => selection.selected.has(key)).length
  const all = keys.length > 0 && ticked === keys.length

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = ticked > 0 && !all
  }, [ticked, all])

  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input m-0"
      style={CHECKBOX_STYLE}
      checked={all}
      disabled={!keys.length}
      onChange={() => selection.setMany(keys, !all)}
      title={all ? 'Untick all shown rows' : 'Tick all shown rows'}
      aria-label="Select all shown rows"
    />
  )
}

/** A select that applies its choice to the ticked rows and resets, ready for the next one. */
export const BulkSelect = ({ placeholder, options, onPick }) => (
  <CFormSelect
    size="sm"
    value=""
    onChange={(e) => e.target.value && onPick(e.target.value)}
    style={{ width: 'auto', minWidth: 190 }}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </CFormSelect>
)

/** Shown above a table while rows are ticked. */
export const BulkBar = ({ count, onClear, children }) =>
  count > 0 ? (
    <div
      className="d-flex align-items-center flex-wrap gap-2 rounded-3 px-3 py-2 mb-2"
      style={{
        backgroundColor: 'var(--cui-primary-bg-subtle, #e7f1ff)',
        border: '1px solid var(--cui-primary-border-subtle, #b6d4fe)',
      }}
    >
      <span className="fw-semibold small me-1">{count} selected</span>
      {children}
      <CButton size="sm" color="secondary" variant="ghost" className="ms-auto" onClick={onClear}>
        Clear selection
      </CButton>
    </div>
  ) : null

/** Tells the admin what a bulk change did, including rows it could not apply to. One toast -
 *  the toast helper closes the previous one. */
export const reportBulk = (applied, refused, what) => {
  const rows = (n) => `${n} ${n === 1 ? 'row' : 'rows'}`
  const done = applied ? `${what} set on ${rows(applied)}.` : `${what} was not set.`
  if (refused) {
    ToastComponent(
      `${done} ${rows(refused)} left as before - that option does not apply.`,
      'warning',
    )
  } else if (applied) {
    ToastComponent(done, 'success')
  }
}
